import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  makeStyles,
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import CloseIcon from '@material-ui/icons/Close'
import { useQuery } from 'react-query'
import getPlayerSeasonStats from '../../Apis/stats/getPlayerSeasonStats'
import QueryKeys from '../../utils/constants/queryKeys'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import StatsGrid from './StatsGrid'
import PlayerSeasonSummaryTable from './PlayerSeasonSummaryTable'
import PlayerGameLogTable from './PlayerGameLogTable'
import statsTableStyles from './statsTable.styles'
import COLORS from '../../utils/constants/colors'

const useDialogStyles = makeStyles({
  dialogPaper: {
    margin: 16,
    width: 'calc(100% - 32px)',
    maxWidth: 'none',
    height: 'calc(100% - 32px)',
    maxHeight: 'none',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
  },
  titleText: {
    fontWeight: 700,
    fontSize: 20,
  },
  subTitle: {
    fontSize: 14,
    color: COLORS.darkGrey,
    marginTop: 2,
  },
  loader: {
    fill: COLORS.orange,
    width: 70,
    height: 70,
  },
  loaderWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
  },
})

const useStatsStyles = makeStyles(statsTableStyles)

const gridFromAverages = (a = {}) => ({
  avgPoints: a.points !== undefined ? Number(a.points).toFixed(1) : '-',
  avgRebounds: a.totalRebounds !== undefined ? Number(a.totalRebounds).toFixed(1) : '-',
  avgAssists: a.assists !== undefined ? Number(a.assists).toFixed(1) : '-',
  avgSteals: a.steals !== undefined ? Number(a.steals).toFixed(1) : '-',
  avgBlocksAgainst: a.blocks !== undefined ? Number(a.blocks).toFixed(1) : '-',
  avgPir: a.pir !== undefined ? Number(a.pir).toFixed(1) : '-',
})

const SeasonDetailDialog = ({ open, onClose, playerId, orgId, season, competitionName }) => {
  const classes = useDialogStyles()
  const statsClasses = useStatsStyles()
  const [selectedStageId, setSelectedStageId] = useState(null)

  const { data: seasonStatsData, isFetching } = useQuery(
    [QueryKeys.GET_PLAYER_SEASON_STATS, orgId, playerId, season?.seasonId, season?.teamId],
    () => getPlayerSeasonStats({ orgId, playerId, seasonId: season.seasonId, teamId: season.teamId }),
    { enabled: open && Boolean(orgId) && Boolean(playerId) && Boolean(season?.seasonId) },
  )

  const seasonStats = seasonStatsData?.data
  const stages = seasonStats?.stages || []
  const seasonDisplayStats = seasonStats?.displayStats

  // Reset stage selection when the dialog target season/team changes.
  useEffect(() => {
    setSelectedStageId(null)
  }, [season?.seasonId, season?.teamId])

  useEffect(() => {
    if (stages.length > 0 && !stages.some(s => s.stageId === selectedStageId)) {
      setSelectedStageId(stages[0].stageId)
    }
  }, [stages, selectedStageId])

  const selectedStage = stages.find(s => s.stageId === selectedStageId) || null
  const displayTotals = selectedStage?.totals || seasonStats?.totals
  const displayAverages = selectedStage?.averages || seasonStats?.averages
  const displayGames = selectedStage?.games || []

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      fullWidth
    >
      <DialogTitle disableTypography className={classes.title}>
        <div>
          <Typography className={classes.titleText}>
            {seasonStats?.seasonName || season?.seasonName || 'Season'}
          </Typography>
          {(season?.teamName || competitionName) && (
            <Typography className={classes.subTitle}>
              {[competitionName, season?.teamName].filter(Boolean).join(' • ')}
            </Typography>
          )}
        </div>
        <IconButton onClick={onClose} size='small'>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isFetching ? (
          <div className={classes.loaderWrap}>
            <LoaderDots className={classes.loader} />
          </div>
        ) : !seasonStats ? (
          <Box textAlign='center' padding='40px 20px'>
            <Typography variant='h6' color='textSecondary'>
              No season statistics available
            </Typography>
          </Box>
        ) : (
          <>
            <Box marginBottom='24px'>
              <StatsGrid
                stats={gridFromAverages(displayAverages || {})}
                showValue='average'
                displayStats={seasonDisplayStats}
              />
            </Box>

            <Typography className={statsClasses.sectionHeading}>
              {seasonStats.seasonName} season stats
            </Typography>
            <PlayerSeasonSummaryTable
              totals={displayTotals}
              averages={displayAverages}
              displayStats={seasonDisplayStats}
            />

            <Box className={statsClasses.gameStatsHeaderRow}>
              <Typography className={statsClasses.sectionHeading}>
                Game stats
              </Typography>
              {stages.length > 0 && (
                <ToggleButtonGroup
                  value={selectedStageId}
                  exclusive
                  size='small'
                  onChange={(_, value) => { if (value) setSelectedStageId(value) }}
                  className={statsClasses.stageToggle}
                >
                  {stages.map(stage => (
                    <ToggleButton
                      key={stage.stageId}
                      value={stage.stageId}
                      classes={{
                        root: statsClasses.stageToggleButton,
                        selected: statsClasses.stageToggleButtonSelected,
                      }}
                    >
                      {stage.stageName}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            </Box>

            {displayGames.length === 0 ? (
              <Box textAlign='center' padding='20px'>
                <Typography color='textSecondary'>
                  No games for this stage
                </Typography>
              </Box>
            ) : (
              <PlayerGameLogTable games={displayGames} displayStats={seasonDisplayStats} />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

SeasonDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  playerId: PropTypes.string,
  orgId: PropTypes.string,
  season: PropTypes.shape({
    seasonId: PropTypes.string,
    teamId: PropTypes.string,
    seasonName: PropTypes.string,
    teamName: PropTypes.string,
  }),
  competitionName: PropTypes.string,
}

export default SeasonDetailDialog
